import { useCallback, useEffect, useRef } from 'react'
import type { RefObject } from 'react'
import type { AgGridReact } from 'ag-grid-react'
import type {
  CellClassParams,
  CellMouseDownEvent,
  CellMouseOverEvent,
  CellStyle,
} from 'ag-grid-community'

type Range = { r1: number; r2: number; c1: number; c2: number }

/**
 * Кросс-браузерное копирование текста в буфер обмена.
 * navigator.clipboard в Firefox требует user-gesture и иногда падает —
 * fallback через временный textarea + document.execCommand('copy').
 */
export function copyTextToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text).catch(() => {
      fallbackCopy(text)
    })
  }
  fallbackCopy(text)
  return Promise.resolve()
}

function fallbackCopy(text: string): void {
  const ta = document.createElement('textarea')
  ta.value = text
  ta.setAttribute('readonly', '')
  ta.style.position = 'fixed'
  ta.style.top = '0'
  ta.style.left = '0'
  ta.style.opacity = '0'
  document.body.appendChild(ta)
  const prevActive = document.activeElement as HTMLElement | null
  ta.focus()
  ta.select()
  try {
    document.execCommand('copy')
  } catch {
    /* браузер запретил — ничего не делаем */
  }
  document.body.removeChild(ta)
  prevActive?.focus?.()
}

/**
 * Excel-style выделение диапазона ячеек + Ctrl+C / Cmd+C → TSV в буфер.
 * Универсальный read-only хук для любых AG Grid таблиц проекта.
 *
 * Как пользоваться:
 *   const { onCellMouseDown, onCellMouseOver, getCellStyle } = useGridSelection(gridRef)
 *   <AgGridReact
 *     ref={gridRef}
 *     onCellMouseDown={onCellMouseDown}
 *     onCellMouseOver={onCellMouseOver}
 *     defaultColDef={{ ..., cellStyle: getCellStyle }}
 *   />
 *
 * Поведение:
 *   клик          → выделена одна ячейка (становится якорем)
 *   Shift+клик    → прямоугольный диапазон от якоря до клика
 *   drag мышью    → расширение диапазона
 *   Ctrl/Cmd+C    → копирование TSV в буфер (вставится в Excel/Sheets)
 *   клик вне грида → сброс выделения
 */
export function useGridSelection<TData = unknown>(gridRef: RefObject<AgGridReact<TData>>) {
  const selRef = useRef<Range | null>(null)
  const anchorRef = useRef<{ row: number; col: number } | null>(null)
  const dragStartRef = useRef<{ row: number; col: number } | null>(null)
  const draggingRef = useRef(false)

  const getColIdx = useCallback(
    (colId: string): number => {
      const cols = gridRef.current?.api?.getAllDisplayedColumns()
      if (!cols) return -1
      return cols.findIndex((c) => c.getColId() === colId)
    },
    [gridRef],
  )

  const getColIdByIdx = useCallback(
    (idx: number): string | null => {
      const cols = gridRef.current?.api?.getAllDisplayedColumns()
      return cols?.[idx]?.getColId() ?? null
    },
    [gridRef],
  )

  const refresh = useCallback(() => {
    gridRef.current?.api?.refreshCells({ force: true })
  }, [gridRef])

  // mouseup глобально — выходим из drag-режима.
  useEffect(() => {
    const up = () => {
      draggingRef.current = false
      dragStartRef.current = null
    }
    document.addEventListener('mouseup', up, true)
    return () => document.removeEventListener('mouseup', up, true)
  }, [])

  // Ctrl+C / Cmd+C — копирование диапазона в TSV.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Проверяем по e.code (физическая клавиша) — независимо от раскладки
      // клавиатуры. e.key при русской раскладке даёт 'с' (кириллическая «эс»),
      // и проверка по e.key.toLowerCase() === 'c' пропускала событие — копирование
      // не работало у пользователей с русской раскладкой включённой в момент Ctrl+C.
      if (!(e.ctrlKey || e.metaKey) || e.code !== 'KeyC') return
      // Если фокус в input/textarea/contentEditable И ТАМ ВЫДЕЛЕН ТЕКСТ — пусть
      // браузер копирует его (стандартное поведение). Если ничего не выделено —
      // копируем наше grid-выделение.
      const active = document.activeElement as HTMLElement | null
      const isTextInput =
        active &&
        (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)
      if (isTextInput) {
        const el = active as HTMLInputElement | HTMLTextAreaElement
        const hasSelection =
          typeof el.selectionStart === 'number' &&
          typeof el.selectionEnd === 'number' &&
          el.selectionStart !== el.selectionEnd
        if (hasSelection) return
      }

      const api = gridRef.current?.api
      const sel = selRef.current
      if (!api || !sel) return

      const rows: string[] = []
      for (let r = sel.r1; r <= sel.r2; r++) {
        const rowNode = api.getDisplayedRowAtIndex(r)
        if (!rowNode) continue
        const cells: string[] = []
        for (let c = sel.c1; c <= sel.c2; c++) {
          const colId = getColIdByIdx(c)
          if (!colId) {
            cells.push('')
            continue
          }
          const col = api.getColumn(colId)
          if (!col) {
            cells.push('')
            continue
          }
          const value = api.getCellValue({ rowNode, colKey: col, useFormatter: true })
          cells.push(value == null ? '' : String(value))
        }
        rows.push(cells.join('\t'))
      }
      const tsv = rows.join('\n')
      if (!tsv) return
      void copyTextToClipboard(tsv)
      e.preventDefault()
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [gridRef, getColIdByIdx])

  const onCellMouseDown = useCallback(
    (e: CellMouseDownEvent) => {
      if (e.rowIndex == null) return
      const c = getColIdx(e.column.getColId())
      if (c < 0) return
      const ev = e.event as MouseEvent | undefined

      // Shift+клик расширяет диапазон от якоря.
      if (ev?.shiftKey && anchorRef.current) {
        const { row: ar, col: ac } = anchorRef.current
        selRef.current = {
          r1: Math.min(ar, e.rowIndex),
          r2: Math.max(ar, e.rowIndex),
          c1: Math.min(ac, c),
          c2: Math.max(ac, c),
        }
        refresh()
        return
      }

      // Клик по интерактивному элементу — не перехватываем.
      const target = ev?.target as HTMLElement | null
      if (target?.closest('button, a, input, select, textarea')) return

      dragStartRef.current = { row: e.rowIndex, col: c }
      draggingRef.current = true
      anchorRef.current = { row: e.rowIndex, col: c }
      selRef.current = { r1: e.rowIndex, r2: e.rowIndex, c1: c, c2: c }
      refresh()
    },
    [getColIdx, refresh],
  )

  const onCellMouseOver = useCallback(
    (e: CellMouseOverEvent) => {
      if (!draggingRef.current || !dragStartRef.current || e.rowIndex == null) return
      const c = getColIdx(e.column.getColId())
      if (c < 0) return
      const { row: sr, col: sc } = dragStartRef.current
      selRef.current = {
        r1: Math.min(sr, e.rowIndex),
        r2: Math.max(sr, e.rowIndex),
        c1: Math.min(sc, c),
        c2: Math.max(sc, c),
      }
      refresh()
    },
    [getColIdx, refresh],
  )

  // Подсветка ячеек диапазона. Цвет берём из CSS-переменной — она per-theme.
  // Для ячеек вне диапазона возвращаем явный transparent (иначе AG Grid не
  // сбрасывает inline-стиль при перерисовке).
  const getCellStyle = useCallback(
    (params: CellClassParams): CellStyle => {
      const sel = selRef.current
      if (!sel || params.rowIndex == null) return { background: 'transparent' }
      const c = getColIdx(params.column.getColId())
      if (c < 0) return { background: 'transparent' }
      const inside =
        params.rowIndex >= sel.r1 && params.rowIndex <= sel.r2 && c >= sel.c1 && c <= sel.c2
      return inside
        ? { background: 'var(--crm-cell-selected-bg, rgba(16, 185, 129, 0.14))' }
        : { background: 'transparent' }
    },
    [getColIdx],
  )

  // Сброс выделения при клике вне ячеек/шапки.
  useEffect(() => {
    const onDocDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (!target) return
      if (target.closest('.ag-cell')) return
      if (target.closest('.ag-header')) return
      if (selRef.current) {
        selRef.current = null
        anchorRef.current = null
        refresh()
      }
    }
    document.addEventListener('mousedown', onDocDown, true)
    return () => document.removeEventListener('mousedown', onDocDown, true)
  }, [refresh])

  return { onCellMouseDown, onCellMouseOver, getCellStyle, selRef }
}
